import { rememberStartedTask } from '@/store/backgroundTasks/startedTasks';
import apiInstance from '@api/index';
import toastMessage from '@components/Toast';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { MemoSubToggleModes } from '../../components/DeliverableMemos';
import { DeliverableReportModes } from '../../components/DeliverableReports';

import {
  AsyncThunkConfig,
  GenericResponse,
  RunDeliverableMemosParams,
  ScheduleDeliverableParams,
  StartedTaskResponse,
  ThunkError,
} from './types';
import { EntityJECurrenciesSuccessPayload } from '@/store/companyEntity/types';
import { fetchBulkTransactionTitles } from '@pages/HedgeAccountingAdmin/store/hedgeAccountingSlice';
import { BulkTransactionTitlesRequest } from '@pages/HedgeAccountingAdmin/store/types';
import { AppDispatch } from '@store/store';

export const scheduleDeliverable = createAsyncThunk<
  GenericResponse,
  ScheduleDeliverableParams,
  ThunkError
>('deliverables/scheduleDeliverable', async (params, { rejectWithValue }) => {
  try {
    toastMessage.info('Scheduling Deliverable...');
    const response =
      await apiInstance.runDeliverables.scheduleDeliverable(params);
    if (!response.success) {
      throw new Error(response.error_msg || 'Deliverable scheduling failed');
    }
    toastMessage.success('Deliverable scheduled successfully');
    return response;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.error_msg ||
      'An unknown error occurred while scheduling deliverable';
    toastMessage.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const toggleReportTypeThunk = createAsyncThunk<
  { companyEntityId: string | number; reportType: DeliverableReportModes },
  { companyEntityId: string | number; reportType: DeliverableReportModes },
  AsyncThunkConfig
>(
  'runDeliverables/toggleReportType',
  async ({ companyEntityId, reportType }) => {
    return { companyEntityId, reportType };
  },
);

export const toggleMemoToggleThunk = createAsyncThunk<
  { companyEntityId: string; memoToggle: MemoSubToggleModes },
  { companyEntityId: string; memoToggle: MemoSubToggleModes },
  AsyncThunkConfig
>(
  'runDeliverables/toggleMemoToggle',
  async ({ companyEntityId, memoToggle }) => {
    return { companyEntityId, memoToggle };
  },
);

export const toggleTransactionSelection = createAsyncThunk<
  {
    companyEntityId: string | number;
    transactionId: string | number;
    selected: boolean;
  },
  {
    companyEntityId: string | number;
    transactionId: string | number;
    selected: boolean;
  },
  AsyncThunkConfig
>(
  'deliverables/toggleTransactionSelection',
  async ({ companyEntityId, transactionId, selected }, { rejectWithValue }) => {
    try {
      return { companyEntityId, transactionId, selected };
    } catch (error: any) {
      return rejectWithValue('Error al actualizar la selección');
    }
  },
);

export const clearReportsTogglesThunk = createAsyncThunk<
  { companyEntityId: string },
  { companyEntityId: string },
  AsyncThunkConfig
>('runDeliverables/clearReportsToggles', async ({ companyEntityId }) => {
  return { companyEntityId };
});

export const clearMemoTogglesThunk = createAsyncThunk<
  { companyEntityId: string },
  { companyEntityId: string },
  AsyncThunkConfig
>('runDeliverables/clearMemoToggles', async ({ companyEntityId }) => {
  return { companyEntityId };
});

export const runDeliverableReports = createAsyncThunk<
  GenericResponse,
  ScheduleDeliverableParams & { isSchedule?: boolean },
  ThunkError
>(
  'deliverables/runDeliverableReports',
  async (params, { rejectWithValue }) => {
    try {
      // isSchedule is a caller-side flag, not part of the request body.
      const { isSchedule: _isSchedule, ...deliverableParams } = params;
      const response =
        await apiInstance.runDeliverables.runDeliverableReports(
          deliverableParams,
        );
      if (!response.success) {
        throw new Error(
          response.error_msg || 'Run Deliverable execution failed',
        );
      }
      // The endpoint no longer builds the workbook inline: it starts a background task and returns
      // its id. Progress, cancellation and the download live in the Background Tasks bell; this tab
      // remembers the id so it can still download the file automatically when the run finishes.
      const { task_id: taskId, reattached } = (response.data ??
        {}) as StartedTaskResponse;
      if (taskId) {
        rememberStartedTask(taskId);
      }
      toastMessage.info(
        reattached
          ? 'This deliverable run is already in progress — track it in Background tasks.'
          : 'Deliverable generation started — track it in Background tasks.',
      );
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error_msg ||
        'An unknown error occurred while running deliverable';
      toastMessage.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

export const runDeliverableMemos = createAsyncThunk<
  GenericResponse,
  RunDeliverableMemosParams,
  ThunkError
>('deliverables/runDeliverableMemos', async (params, { rejectWithValue }) => {
  try {
    const response = await apiInstance.runDeliverables.runDeliverableMemos(params);
    if (!response.success) {
        const errorMessage = response.error_msg || 'Run Deliverable Memos execution failed';
        toastMessage.error(errorMessage);
        return rejectWithValue(errorMessage);
    }
    // Memos run as their own background task; when reports were requested too, this one is queued
    // behind them so the two never compete for the same CPU.
    const { task_id: taskId, reattached } = (response.data ??
      {}) as StartedTaskResponse;
    if (taskId) {
      rememberStartedTask(taskId);
    }
    toastMessage.info(
      reattached
        ? 'These memos are already being generated — track them in Background tasks.'
        : 'Designation memos queued — track them in Background tasks.',
    );
    return response;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.error_msg ||
      'An unknown error occurred while running deliverable memos';
    toastMessage.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const getEntityCurrency = createAsyncThunk<
  EntityJECurrenciesSuccessPayload,
  string | undefined | number,
  ThunkError
>('deliverables/getEntityCurrency', async (entityId, { rejectWithValue }) => {
  try {
    const response =
      await apiInstance.runDeliverables.getJECurrenciesByEntity(entityId);
    return response;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.error_msg ||
      'An unknown error occurred while getting entity currency';
    toastMessage.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const fetchScheduledTransactions = createAsyncThunk<
  { success: boolean; data: Record<string, any[]> },
  { company_entity_ids: string[] },
  ThunkError
>(
  'deliverables/fetchScheduledTransactions',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getTradesWithDeliverables(params);
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error_msg ||
        'An unknown error occurred while fetching scheduled transactions';
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchBulkTransactionTitlesForDeliverables = createAsyncThunk<
  void,
  BulkTransactionTitlesRequest,
  { dispatch: AppDispatch; rejectValue: string }
>(
  'deliverables/fetchBulkTransactionTitlesForDeliverables',
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const result = await dispatch(fetchBulkTransactionTitles(params));
      if (fetchBulkTransactionTitles.fulfilled.match(result)) {
        const { transaction_titles } = result.payload;
        if (!transaction_titles || transaction_titles.length === 0) {
          toastMessage.warning(
            'No Live/Closed trades found for this entity and no hedge accounting is set up',
          );
        }
      }
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch bulk transaction titles',
      );
    }
  },
);
